"use client";
import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  ArrowRightIcon,
  CodeBracketIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon
} from "@heroicons/react/24/outline";
import { FaGithub, FaLinkedin, FaInstagram } from "react-icons/fa";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    contact: [
      { 
        label: "Email", 
        href: "mailto:nirmaldarekar90@gmail.com", 
        value: "nirmaldarekar90@gmail.com.com",
        icon: EnvelopeIcon 
      },
      { 
        label: "Phone", 
        href: "tel:9833921091", 
        value: "9833921091",
        icon: PhoneIcon 
      },
      { 
        label: "Kalyan, Maharashtra, India", 
        value: "Kalyan, Maharashtra, India",
        icon: MapPinIcon 
      }
    ],
    quickLinks: [
      { 
        label: "About", 
        href: "#about",
        icon: UserCircleIcon 
      },
      { 
        label: "Projects", 
        href: "#projects",
        icon: CodeBracketIcon 
      },
      { 
        label: "Contact", 
        href: "#contact",
        icon: ChatBubbleLeftRightIcon 
      }
    ],
    social: [
      { 
        label: "GitHub", 
        href: "https://github.com/Nirmal21D", 
        icon: "github",
        color: "hover:bg-gray-900 hover:text-white"
      },
      { 
        label: "LinkedIn", 
        href: "https://www.linkedin.com/in/nirmal-darekar", 
        icon: "linkedin",
        color: "hover:bg-blue-600 hover:text-white"
      },
      { 
        label: "Instagram", 
        href: "https://www.instagram.com/nirmal_darekar", 
        icon: "instagram",
        color: "hover:bg-pink-600 hover:text-white"
      }
    ]
  };

  const getSocialIcon = (icon) => {
    switch (icon) {
      case "github":
        return <FaGithub className="w-5 h-5" />;
      case "linkedin":
        return <FaLinkedin className="w-5 h-5" />;
      case "instagram":
        return <FaInstagram className="w-5 h-5" />;
      default:
        return null;
    }
  };

  return (
    <footer className="w-full py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-[#020817] border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <EnvelopeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </span>
              <span>Contact</span>
            </h3>
            <ul className="space-y-4">
              {footerLinks.contact.map((item, index) => (
                <motion.li
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-3 group"
                >
                  <span className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors duration-200">
                    <item.icon className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200" />
                  </span>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <span className="text-gray-600 dark:text-gray-300">{item.value}</span>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <ArrowRightIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </span>
              <span>Quick Links</span>
            </h3>
            <ul className="space-y-4">
              {footerLinks.quickLinks.map((link, index) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group"
                >
                  <Link
                    href={link.href}
                    className="flex items-center space-x-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                  >
                    <span className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors duration-200">
                      <link.icon className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200" />
                    </span>
                    <span>{link.label}</span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </span>
              <span>Connect</span>
            </h3>
            <div className="flex flex-unwrap gap-4">
              {footerLinks.social.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-900 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 dark:border-gray-800 ${social.color}`}
                >
                  {getSocialIcon(social.icon)}
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{social.label}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-800 text-center"
        >
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            © {currentYear} Nirmal Darekar. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;